import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Bot, Braces, Check, Globe, ShieldCheck, Smartphone, Zap, type IconComponent } from '@/components/icons';

/**
 * Sign-in illustration: "1QA tests your product from every side." The product sits in the centre; each kind
 * of testing lights up in turn, sends a check down its line into the product and gets a green tick, while a
 * coverage bar fills to 6/6. Drawn on an 800 x 500 grid (SVG for rings and lines, HTML for the nodes) inside
 * an 8:5 box so nothing is stretched. Decorative (aria-hidden); with reduced motion it is a still, all-passed map.
 */

const W = 800;
const H = 500;
const CENTER = { x: 400, y: 250 };

/** `soon`: 1QA can't run this kind of test yet (only web and mobile can). */
const KINDS: { label: string; Icon: IconComponent; x: number; y: number; soon?: boolean }[] = [
  { label: 'Web', Icon: Globe, x: 140, y: 165 },
  { label: 'Mobile', Icon: Smartphone, x: 400, y: 70 },
  { label: 'API', Icon: Braces, x: 660, y: 165, soon: true },
  { label: 'Performance', Icon: Zap, x: 660, y: 335 },
  { label: 'Security', Icon: ShieldCheck, x: 400, y: 405, soon: true },
  { label: 'LLM', Icon: Bot, x: 140, y: 335, soon: true },
];

const PULSE_MS = 900; // a check travels from its node to the product
const STEP_MS = 1500; // one kind of testing per step
const HOLD_MS = 2600; // "all checks passed" before starting over

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const pct = (v: number, of: number) => `${(v / of) * 100}%`;

export function CoverageIllustration() {
  const [reduced] = useState(prefersReducedMotion);
  // active = the kind currently sending its check; checked = how many kinds have passed.
  const [active, setActive] = useState(reduced ? -1 : 0);
  const [checked, setChecked] = useState(reduced ? KINDS.length : 0);

  useEffect(() => {
    if (reduced) return;
    if (active === -1) {
      const t = window.setTimeout(() => {
        setChecked(0);
        setActive(0);
      }, HOLD_MS);
      return () => window.clearTimeout(t);
    }
    const arrive = window.setTimeout(() => setChecked(active + 1), PULSE_MS);
    const next = window.setTimeout(() => setActive(active + 1 < KINDS.length ? active + 1 : -1), STEP_MS);
    return () => {
      window.clearTimeout(arrive);
      window.clearTimeout(next);
    };
  }, [active, reduced]);

  const all = checked === KINDS.length;

  return (
    <div aria-hidden className="select-none">
      <div className="relative aspect-[8/5] w-full">
        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 size-full overflow-visible">
          <defs>
            <radialGradient id="cov-sweep" gradientUnits="userSpaceOnUse" cx={CENTER.x} cy={CENTER.y} r={300}>
              <stop offset="0" style={{ stopColor: 'var(--color-primary)' }} stopOpacity="0.18" />
              <stop offset="1" style={{ stopColor: 'var(--color-primary)' }} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Orbits and a slow radar sweep around the product. */}
          <ellipse cx={CENTER.x} cy={CENTER.y} rx={300} ry={185} fill="none" style={{ stroke: 'var(--color-border)' }} strokeDasharray="2 8" />
          <ellipse cx={CENTER.x} cy={CENTER.y} rx={150} ry={92} fill="none" style={{ stroke: 'var(--color-border)' }} />
          {!reduced && (
            <g style={{ transformOrigin: `${CENTER.x}px ${CENTER.y}px` }} className="animate-radar">
              <path d={`M ${CENTER.x} ${CENTER.y} L ${CENTER.x + 300} ${CENTER.y} A 300 185 0 0 0 ${CENTER.x + 212} ${CENTER.y - 131} Z`} fill="url(#cov-sweep)" />
            </g>
          )}
          {/* Lines from each kind of testing into the product. */}
          {KINDS.map((k, i) => {
            const on = i === active;
            const done = i < checked;
            return (
              <line
                key={k.label}
                x1={k.x}
                y1={k.y}
                x2={CENTER.x}
                y2={CENTER.y}
                strokeWidth={on ? 1.5 : 1}
                strokeDasharray="4 6"
                className={clsx('transition-[stroke] duration-500', !reduced && 'animate-dash-flow')}
                style={{ stroke: on ? 'var(--color-primary)' : done ? 'color-mix(in srgb, var(--color-success) 55%, transparent)' : 'var(--color-border)' }}
              />
            );
          })}
          {/* The check travelling from the active kind to the product. */}
          {!reduced && active >= 0 && (
            <circle key={active} r="5" style={{ fill: 'var(--color-primary)' }}>
              <animateMotion dur={`${PULSE_MS}ms`} fill="freeze" path={`M ${KINDS[active]!.x} ${KINDS[active]!.y} L ${CENTER.x} ${CENTER.y}`} />
            </circle>
          )}
        </svg>

        {/* The product: an app window. */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: pct(CENTER.x, W), top: pct(CENTER.y, H) }}>
          {!reduced && checked > 0 && <span key={checked} className="absolute inset-0 animate-arrive rounded-card ring-2 ring-primary" />}
          <div className={clsx('relative w-24 rounded-card border bg-surface p-2 transition-colors duration-500', all ? 'border-success' : 'border-border')}>
            <div className="mb-1.5 flex gap-1">
              <span className="size-1.5 rounded-full bg-fg-muted" />
              <span className="size-1.5 rounded-full bg-fg-muted" />
              <span className="size-1.5 rounded-full bg-fg-muted" />
            </div>
            <div className="space-y-1">
              <div className="h-1.5 w-3/4 rounded-bar bg-surface-raised" />
              <div className="h-1.5 w-full rounded-bar bg-surface-raised" />
              <div className="h-1.5 w-1/2 rounded-bar bg-primary/60" />
            </div>
          </div>
          <div className={clsx('mt-2 text-center text-[10px] font-semibold tracking-label uppercase transition-colors duration-500', all ? 'text-success' : 'text-fg-secondary')}>Your app</div>
        </div>

        {/* The kinds of testing. */}
        {KINDS.map((k, i) => {
          const on = i === active;
          const done = i < checked;
          return (
            <div key={k.label} className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center" style={{ left: pct(k.x, W), top: pct(k.y, H) }}>
              <div className="relative">
                <span
                  className={clsx(
                    'grid size-11 place-items-center rounded-full border bg-surface transition-colors duration-300',
                    on ? 'border-primary bg-primary-subtle text-primary' : done ? 'border-success/60 text-fg' : 'border-border text-fg-muted',
                  )}
                >
                  <k.Icon className="size-4" />
                </span>
                <span
                  className={clsx(
                    'absolute -right-1 -bottom-1 grid size-4 place-items-center rounded-full bg-success text-background transition-[opacity,scale] duration-300',
                    done ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
                  )}
                >
                  <Check className="size-2.5" />
                </span>
              </div>
              <span className={clsx('mt-1.5 text-[10px] font-semibold tracking-label whitespace-nowrap uppercase transition-colors duration-300', on || done ? 'text-fg' : 'text-fg-muted')}>
                {k.label}
              </span>
              {k.soon && <span className="text-[10px] leading-3 text-fg-muted">soon</span>}
            </div>
          );
        })}
      </div>

      {/* Coverage meter. */}
      <div className="mt-6 flex items-center gap-4">
        <span className="text-[10px] font-semibold tracking-label text-fg-muted uppercase">Coverage</span>
        <div className="flex flex-1 gap-1">
          {KINDS.map((k, i) => (
            <span key={k.label} className={clsx('h-1.5 flex-1 rounded-bar transition-colors duration-500', i < checked ? 'bg-success' : 'bg-surface-raised')} />
          ))}
        </div>
        <span className={clsx('w-36 text-right text-[10px] font-semibold tracking-label uppercase tabular-nums', all ? 'text-success' : 'text-fg-secondary')}>
          {all ? '6/6 · All checks passed' : `${checked}/${KINDS.length} checked`}
        </span>
      </div>
    </div>
  );
}
